// Shared state of the report editor. Tabs read and edit `state.tpl` directly (it is the template
// being worked on, not saved until the user presses Save).
import Vue from 'vue'
import axios from 'axios'

export const state = Vue.observable({
  id: null, // saved report id, null while it is a new report
  tpl: null, // the template being edited
  ctx: 'main', // which part the Data / Layout / Output tabs edit: 'main' or a drill-down id
  meta: null, // { macroDocs, userTypes, ... } from the server
  categories: [],
  problems: [], // last validation / save problems
  lastColumns: {}, // `${ctx}.${dataset}` -> columns detected by a preview run
  previewParams: {}, // filter values used by the "detect columns" / preview actions
  paramOptions: {}, // options of select filters that load from the database
  designer: { open: false, index: 0 }, // the print designer dialog
  sample: { ctx: null, result: null, loading: false, error: '' } // a real run of the report, used by the designers
})

export const SECTION_TYPES = [
  { value: 'kpi', text: 'Key figures (tiles)', icon: 'mdi-numeric' },
  { value: 'table', text: 'Table', icon: 'mdi-table' },
  { value: 'chart', text: 'Chart', icon: 'mdi-chart-bar' },
  { value: 'text', text: 'Text / note', icon: 'mdi-text' }
]

export const FORMATS = ['text', 'number', 'integer', 'currency', 'percent', 'date', 'datetime', 'boolean']
export const RECORD_LINK_TYPES = ['order', 'customer', 'product', 'return', 'purchaseOrder', 'receivingReport', 'inventoryTransfer',
  'dispatch', 'expense', 'user', 'location', 'supplier', 'hirePurchase', 'stocktake', 'brand', 'category', 'promotion', 'serial']

export function newTemplate () {
  return {
    schemaVersion: 1,
    key: 'custom.new-report',
    name: 'New report',
    description: 'Describe in one sentence what this report shows.',
    category: 'Custom',
    tags: [],
    order: 0,
    visibility: 'draft',
    permissions: { userTypeIds: [] },
    urlFilters: true,
    params: [{ name: 'range', type: 'daterange', label: 'Sealed between', required: true, default: { from: 'startOfMonth', to: 'today' } }],
    info: [],
    datasets: [{
      name: 'daily',
      description: 'Sales per day',
      sql: 'SELECT TO_CHAR(m."sealedAt", \'YYYY-MM-DD\') AS day, COUNT(*) AS orders, SUM(m."grandTotal") AS sales\nFROM ({{orderMoney(range)}}) m\nGROUP BY 1 ORDER BY 1'
    }],
    ui: {
      sections: [{
        id: 's1', type: 'table', title: 'Sales by day', width: 12, dataset: 'daily', showTotals: true,
        columns: [
          { field: 'day', label: 'Day' },
          { field: 'orders', label: 'Orders', format: 'integer', align: 'right', total: 'sum' },
          { field: 'sales', label: 'Sales', format: 'currency', align: 'right', total: 'sum' }
        ]
      }]
    },
    drilldowns: [],
    prints: [{ name: 'Standard', paper: 'A4', orientation: 'portrait', header: { title: true, params: true, info: true, runInfo: true }, footer: { pageNumbers: true } }],
    exports: [{ name: 'Daily sales', type: 'csv', dataset: 'daily', details: true }, { name: 'Daily sales', type: 'xlsx', dataset: 'daily', details: true }],
    tests: { fixtures: {}, cases: [], reconciliations: [] }
  }
}

/** The part of the template the Data / Layout / Output tabs are editing. */
export function container () {
  const t = state.tpl
  if (!t) return null
  if (state.ctx === 'main') return t
  return (t.drilldowns || []).find((d) => d.id === state.ctx) || t
}

export function datasetNames () {
  const c = container()
  return c ? (c.datasets || []).map((d) => d.name) : []
}

/** Columns we know for a dataset (from the last preview), for pickers. */
export function knownColumns (dataset) {
  return state.lastColumns[`${state.ctx}.${dataset}`] || []
}

export function uniqueName (base, existing) {
  let n = 1
  let name = base
  while (existing.includes(name)) name = `${base}${++n}`
  return name
}

/** (Re)load options of select filters that come from a query, for previews. */
export async function refreshParamOptions (endpoint) {
  try {
    const res = await axios.post(`${endpoint}/api/rb-builder/param-options`, { params: state.tpl.params })
    state.paramOptions = res.data.data
  } catch (e) {
    state.paramOptions = {}
  }
}

/**
 * Run the report as it is now (read-only) so the designers can show real figures instead of placeholders.
 * ctx = 'main' or a drill-down id. Result goes to state.sample.
 */
export async function loadSample (endpoint, ctx = state.ctx) {
  state.sample = { ctx, result: null, loading: true, error: '' }
  try {
    const res = await axios.post(`${endpoint}/api/rb-builder/run-draft`, {
      template: state.tpl, params: state.previewParams, drill: ctx === 'main' ? undefined : ctx
    })
    state.sample = { ctx, result: res.data.data, loading: false, error: '' }
  } catch (e) {
    const d = e.response && e.response.data
    state.sample = { ctx, result: null, loading: false, error: (d && (d.msg || d.error)) || e.message }
  }
}
